import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { preflightKeys, preflightListKeyParams } from "@/lib/queries";
import { setReportFavorite } from "../api/preflight.api";
import { patchReportInCaches } from "../lib/report-cache";
import type { PreflightReportSummary } from "../types/preflight.types";

/**
 * Star toggle for one report. The star flips optimistically because it is a
 * personal, reversible preference — waiting on the round trip would make the
 * control feel unresponsive.
 */
export function useFavoriteReport(
  report: Pick<PreflightReportSummary, "id" | "isFavorite">,
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (isFavorite: boolean) => setReportFavorite(report.id, isFavorite),
    onMutate: (isFavorite) => {
      const previousIsFavorite = report.isFavorite;
      patchReportInCaches(queryClient, report.id, { isFavorite });
      return { previousIsFavorite };
    },
    onError: (_error, _vars, context) => {
      if (context) {
        patchReportInCaches(queryClient, report.id, {
          isFavorite: context.previousIsFavorite,
        });
      }
      toast.error(t("preflight.toast.favoriteError"));
    },
    onSuccess: () => {
      // A favorites-only list changes membership, not just row data, so patching
      // the row in place cannot add or remove it.
      void queryClient.invalidateQueries({
        queryKey: preflightKeys.lists(),
        predicate: (query) =>
          preflightListKeyParams(query.queryKey)?.favoritesOnly === true,
      });
    },
    meta: { skipGlobalError: true },
  });
}
